// LangSwitcher — One-tap English ↔ Spanish toggle.
// Uses Google Translate's free embedded widget (loaded in index.html).
// We hide Google's default UI and drive translation via the `googtrans` cookie.
// Reading the cookie on mount lets the chosen language survive page navigation.
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import { loadGoogleTranslate } from "@/lib/googleTranslate";

const COOKIE = "googtrans";
const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
];

function readCookie() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=([^;]+)/);
  if (!m) return "en";
  const parts = decodeURIComponent(m[1]).split("/"); // "/en/es" → ["", "en", "es"]
  return parts[2] || "en";
}

function setCookie(lang) {
  if (typeof document === "undefined") return;
  const v = lang === "en" ? "/en/en" : `/en/${lang}`;
  // Set for current host and parent domain so it persists across subdomains
  const host = window.location.hostname;
  document.cookie = `${COOKIE}=${v}; path=/; max-age=31536000`;
  if (host.split(".").length > 1) {
    const parent = "." + host.split(".").slice(-2).join(".");
    document.cookie = `${COOKIE}=${v}; path=/; domain=${parent}; max-age=31536000`;
  }
}

export default function LangSwitcher({ className = "", testid = "lang-switcher" }) {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    const current = readCookie();
    setLang(current);
    // Returning non-English visitors: load the translator on demand so their
    // language persists. English visitors (and security scanners) never load it.
    if (current && current !== "en") loadGoogleTranslate();
  }, []);

  const switchTo = (code) => {
    if (code === lang) return;
    setCookie(code);
    // On reload the mount effect will lazy-load Google Translate when the cookie
    // is non-English, applying the translation cleanly across the whole SPA.
    window.location.reload();
  };

  return (
    <div
      className={"inline-flex items-center gap-0 select-none " + className}
      data-testid={testid}
      role="group"
      aria-label="Language selector"
    >
      <Globe size={13} className="text-white/55 mr-2 hidden sm:inline" aria-hidden />
      {LANGS.map((l, i) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => switchTo(l.code)}
            aria-pressed={active}
            data-testid={`lang-${l.code}`}
            title={l.name}
            className={
              "px-2 py-1 text-[10.5px] font-extrabold tracking-[0.14em] transition-colors " +
              (active ? "text-white" : "text-white/45 hover:text-white") +
              (i === 0 ? " border-r border-white/15 pr-2.5" : " pl-2.5")
            }
            style={active ? { color: "#FFFFFF" } : undefined}
          >
            {l.label}
          </button>
        );
      })}
      {/* Hidden host for google.translate.TranslateElement to mount into. */}
      <span id="google_translate_element" style={{ display: "none" }} aria-hidden />
    </div>
  );
}
